import React, { useState, useMemo } from 'react';
import { CH_TYPE, chTileValueSx, chSurface, chHairline } from '../../styles/appUi';
import { Box, Typography, Tooltip, LinearProgress, IconButton, TextField, Stack, alpha } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ShieldIcon from '@mui/icons-material/Shield';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { useAppTheme } from '../../hooks/useAppTheme';
import { formatCurrency } from '../../utils/formatters';
import { settingsService } from '../../services/settings.service';
import api from '../../services/api';
import { useLiquidationRisk, SEVERITY_COLOR, LiquidationSeverity } from '../../hooks/useLiquidationRisk';

const CAP_KEY = 'risk.dailyLossCap.v1';

/** GET /dashboard/loss-cap — the figure the server's order gate blocks on (audit A-006). */
interface LossCapZone { zone: 'IN' | 'CRYPTO'; dayStartLabel: string; realisedLoss: number; openLoss: number; positions: number }
interface LossCapStatus { total: number; zones: LossCapZone[]; missingBaselines: number }
const ZONE_NAME: Record<string, string> = { IN: 'Indian exchanges', CRYPTO: 'Crypto (Delta)' };

/** Polls the gate's own loss figure every 60 s while the tab is visible. */
function useLossCapStatus(): LossCapStatus | null {
  const [st, setSt] = useState<LossCapStatus | null>(null);
  React.useEffect(() => {
    let alive = true;
    const load = () => {
      if (document.hidden) return;
      api.get('/dashboard/loss-cap', { headers: { _suppressToast: 'true' } } as any)
        .then((r: any) => { if (alive && r.data?.success) setSt(r.data.data); })
        .catch(() => { /* keep the last figure; the tile says when it has none */ });
    };
    load();
    const id = window.setInterval(load, 60_000);
    return () => { alive = false; window.clearInterval(id); };
  }, []);
  return st;
}

const loadCap = (): number | null => {
  try {
    const v = localStorage.getItem(CAP_KEY);
    if (!v) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};
const saveCap = (n: number | null) => {
  try {
    if (n == null) localStorage.removeItem(CAP_KEY);
    else localStorage.setItem(CAP_KEY, String(n));
  } catch { /* ignore */ }
};

export interface RiskCockpitProps {
  totalOpenRisk: number;     // sum of (entry − SL) × qty per open position (positive = at risk)
  /** Open positions with no stop at all — their risk is unbounded and NOT in totalOpenRisk. */
  positionsWithoutSl?: number;
  todayPnl: number;          // realised P&L of trades CLOSED today (excludes open unrealised)
  availableMargin: number;   // ₹
  openPositions: number;
  /** R80b: total portfolio value (cash + holdings + open positions across all
   *  connected brokers). When provided, renders as a tile ahead of Open Risk. */
  portfolioValue?: number;
  /** Authoritative cap from user settings (Phase B). When provided, this takes
   *  precedence over the local-storage fallback. Null/undefined = unset.   */
  cap?: number | null;
  /** True when backend will hard-block orders when breached. */
  capEnforced?: boolean;
  /** R78: render only the daily-loss-cap box (drop the redundant sub-tiles for
   *  Open Risk / Today P&L / Available Margin / Open Positions — those are
   *  already surfaced in the Trading Controls section just below). */
  capOnly?: boolean;
}

const RiskCockpit: React.FC<RiskCockpitProps> = ({
  totalOpenRisk, positionsWithoutSl = 0, todayPnl, availableMargin, openPositions, portfolioValue, cap: capProp, capEnforced, capOnly,
}) => {
  const { C, isDark } = useAppTheme();
  // Prefer the prop (from user settings); fall back to localStorage for backward compat.
  const initialCap = capProp != null ? capProp : loadCap();
  const [cap, setCap] = useState<number | null>(initialCap);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(cap != null ? String(cap) : '');
  // R-autotrade-breaker — the cap doubles as the auto-trade circuit breaker
  // (the server order gate reads UserSettings.dailyLossCap{,Enabled}). We track
  // enforcement locally for optimistic UI and persist both to the backend.
  const [enforced, setEnforced] = useState<boolean>(!!capEnforced);
  const [saving, setSaving] = useState(false);
  // Keep state in sync if the parent's prop changes (e.g. after settings load)
  React.useEffect(() => {
    if (capProp !== undefined) setCap(capProp);
  }, [capProp]);
  React.useEffect(() => { setEnforced(!!capEnforced); }, [capEnforced]);

  // Usage: how much of the cap is in play? Audit A-006 — this used to be a figure of its
  // own (net realised P&L of trades closed today) while the server gate blocked on a
  // different one. It now shows the gate's figure: per position, the loss made since its
  // exchange's trading day began (realised + open), wins not netted. Until the first
  // answer arrives it falls back to today's realised loss, labelled as such.
  const gate = useLossCapStatus();
  const consumed = gate ? gate.total : Math.max(0, -todayPnl);
  const gateTip = gate
    ? (gate.zones.length
        ? gate.zones.map(z => `${ZONE_NAME[z.zone] || z.zone}, since ${z.dayStartLabel}: closed ${formatCurrency(z.realisedLoss)} + open ${formatCurrency(z.openLoss)} across ${z.positions} position${z.positions === 1 ? '' : 's'}`).join(' · ')
        : 'No live positions traded today.')
    : 'Waiting for the server figure; showing today\'s realised loss meanwhile.';
  const pct = cap && cap > 0 ? Math.min(100, (consumed / cap) * 100) : 0;

  // Open Risk for display: the incoming `totalOpenRisk` is positive when money
  // is AT RISK (sum of (entry − SL) × qty). Users read the headline as a P&L-at-
  // stops figure, so flip the sign → negative = net loss if all stops hit (red),
  // positive = stops already locked in profit (green). This matches the sign of
  // the SL Risk (₹) column summed across open trades.
  // `|| 0` turns -0 into 0, which printed as "-₹0.00".
  const openRiskSigned = -totalOpenRisk || 0;
  const unbounded = positionsWithoutSl > 0;

  const status = useMemo<'safe' | 'warn' | 'danger' | 'unset'>(() => {
    if (!cap) return 'unset';
    if (pct >= 100) return 'danger';
    if (pct >= 75) return 'warn';
    return 'safe';
  }, [cap, pct]);

  const statusColor = status === 'danger' ? C.bear : status === 'warn' ? C.warn : status === 'safe' ? C.bull : C.dim;
  const statusBg = status === 'danger' ? C.bearMuted : status === 'warn' ? C.warnMuted : status === 'safe' ? C.bullMuted : C.dimMuted;

  const commitDraft = async () => {
    const n = draft.trim() === '' ? null : parseFloat(draft);
    const next = n != null && Number.isFinite(n) && n > 0 ? n : null;
    setCap(next);
    saveCap(next);            // local mirror (backward-compat / instant paint)
    setEnforced(next != null);
    setEditing(false);
    // Persist to the ENFORCED backend setting so it actually gates auto-trade.
    // Setting a value also enables enforcement; clearing it disables.
    setSaving(true);
    try {
      await settingsService.updateUserSettings({ dailyLossCap: next as any, dailyLossCapEnabled: next != null } as any);
    } catch { /* surfaced on the next settings refresh */ }
    setSaving(false);
  };

  // Toggle enforcement (ENFORCED ↔ TRACKING) without changing the cap value.
  const toggleEnforce = async () => {
    if (cap == null) return;
    const nextEnforced = !enforced;
    setEnforced(nextEnforced);
    setSaving(true);
    try {
      await settingsService.updateUserSettings({ dailyLossCapEnabled: nextEnforced } as any);
    } catch { /* ignore */ }
    setSaving(false);
  };

  // ── Liquidation risk ───────────────────────────────────────────────────────
  // MMR = maintenance margin / collateral. Delta liquidates at 100%. `risk` is
  // null (or available:false) until the guard's first evaluation — that is
  // UNKNOWN and must not render as reassurance.
  const risk = useLiquidationRisk();
  const liqSeverity: LiquidationSeverity = (risk?.severity as LiquidationSeverity) || 'SAFE';
  const liqKnown = !!risk?.available;
  const liqSevere = liqKnown && (liqSeverity === 'WARNING' || liqSeverity === 'CRITICAL' || liqSeverity === 'EMERGENCY');
  const liqColor = liqKnown ? SEVERITY_COLOR[liqSeverity] : C.neutral;
  const liqValue = !liqKnown
    ? '—'
    : risk?.mmr == null
      ? 'NO COLLATERAL'
      : `${risk.mmr.toFixed(0)}%`;
  const liqTooltip = !liqKnown
    ? 'Margin risk has not been evaluated yet — the guard checks every 30 seconds once your Delta positions are synced. This is NOT a confirmation that you are safe.'
    : [
        `Maintenance margin ratio: ${risk?.mmr == null ? 'no collateral' : risk.mmr.toFixed(1) + '%'} of 100%.`,
        'Delta liquidates your positions when this reaches 100%.',
        risk?.bufferPct != null ? `A ${risk.bufferPct.toFixed(1)}% adverse move across your positions would get there.` : '',
        risk?.fundsRequiredUsd ? `Add about ${formatCurrency(risk.fundsRequiredInr || 0)} to return to your safe level.` : '',
        risk?.stale ? 'WARNING: this figure is stale — the app has not been able to refresh it.' : '',
        risk?.modelUnverified ? 'NOTE: our margin model disagrees with the numbers Delta reports; treat as approximate.' : '',
      ].filter(Boolean).join(' ');

  const Tile: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; tooltip?: string; valueColor?: string; bg?: string; borderColor?: string }> = ({ icon, label, value, tooltip, valueColor, bg, borderColor }) => (
    <Tooltip arrow title={tooltip || ''}>
      <Box sx={{
        flex: '1 1 0',
        minWidth: 120,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        px: 1.25,
        py: 1,
        borderRadius: 1.25,
        bgcolor: bg || chSurface(isDark),
        border: '1px solid',
        borderColor: borderColor || chHairline(isDark),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          {icon}
          <Typography variant="caption" sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ ...chTileValueSx, color: valueColor || 'text.primary' }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        // Offset by the sticky AppBar's height (Toolbar minHeight 48/52) PLUS the
        // tab strip's height when it's showing (--tabstrip-h, set by Layout), so
        // the cockpit freezes flush BELOW the whole header instead of behind it.
        top: { xs: 'calc(48px + var(--tabstrip-h, 0px))', sm: 'calc(52px + var(--tabstrip-h, 0px))' },
        zIndex: 5,
        mb: 1.5,
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: status === 'danger' ? C.bearBorder : status === 'warn' ? C.warnBorder : 'divider',
        /* A real drop shadow, not elevation 1. The strip and the cards beneath
           it share `background.paper`, so content scrolling underneath used to
           read as CLIPPED rather than as passing behind a layer. */
        boxShadow: isDark
          ? '0 6px 14px -6px rgba(0,0,0,0.75)'
          : '0 6px 14px -6px rgba(15,23,42,0.22)',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'stretch' }}>
        {/* Daily loss cap usage */}
        {/* Same flex weight and min-width as every other tile. It used to be
            `flex: '2 1 240px'` against its siblings' `1 1 0`, so it took ~2.5x
            the width of the row's most valuable space — while showing no value
            at all, just "Tap pencil to set cap". */}
        <Box sx={{
          flex: '1 1 0', minWidth: 120, px: 1.25, py: 1, borderRadius: 1.25,
          bgcolor: statusBg, border: '1px solid', borderColor: statusColor,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: statusColor, flexWrap: 'wrap' }}>
              <Tooltip
                arrow
                placement="top"
                title={
                  <Box sx={{ fontSize: CH_TYPE.body, lineHeight: 1.5 }}>
                    <Box sx={{ fontWeight: 700, mb: 0.5 }}>Risk Cockpit — Daily-Loss Cap (auto-trade circuit breaker)</Box>
                    The value you set is your <b>maximum acceptable loss for the day (₹)</b>. The bar fills as
                    <b> today's loss</b> approaches it. That is the same figure the order gate blocks on: for each
                    LIVE position, the loss it made since its exchange's trading day began, closed or still open.
                    A position carried from yesterday counts only today's move. Gains do not offset losses. Paper
                    trades are not counted. Indian exchanges start the day at 00:00 IST; crypto (Delta) at 05:30 IST
                    (00:00 UTC).
                    <Box sx={{ mt: 0.5, fontStyle: 'italic' }}>{gateTip}</Box>
                    Set it with the pencil here (saving turns enforcement ON); click the ENFORCED/TRACKING badge to toggle.
                    <Box sx={{ mt: 0.5 }}>
                      • A <b>higher</b> value allows more loss before it warns/blocks; a <b>lower</b> value tightens it.<br />
                      • When <b>ENFORCED</b>, this is the <b>auto-trade circuit breaker</b>: new <b>auto-entries</b> are
                      <b> blocked</b> once consumed ≥ cap. Exits and stop-losses are <b>never</b> blocked.<br />
                      • When <b>TRACKING</b> only, it's a visual warning — orders are <b>not</b> blocked.
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      Setting or changing this value does <b>not</b> place or close any order by itself — it only governs the
                      auto-entry gate above.
                    </Box>
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                  <ShieldIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: CH_TYPE.small, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Risk Cockpit
                  </Typography>
                </Box>
              </Tooltip>
              {cap != null && (
                <Tooltip arrow title={enforced
                  ? 'ENFORCED — new auto-entries are blocked once the cap is hit (exits/SL still run). Click to switch to tracking-only.'
                  : 'TRACKING only — visual warning, orders are NOT blocked. Click to ENFORCE (block new auto-entries at the cap).'}>
                  <Box component="span" onClick={toggleEnforce} sx={{
                    px: 0.5, py: 0, ml: 0.25, borderRadius: 0.5,
                    fontSize: CH_TYPE.micro, fontWeight: 700, letterSpacing: 0.3,
                    cursor: 'pointer',
                    bgcolor: enforced ? C.bearMuted : C.dimMuted,
                    color: enforced ? C.bear : C.dim,
                  }}>
                    {enforced ? 'ENFORCED' : 'TRACKING'}
                  </Box>
                </Tooltip>
              )}
            </Box>
            {!editing ? (
              <Tooltip arrow title="Set your daily-loss cap (₹). Saving enforces it so it gates auto-trade; clear it to remove.">
                <IconButton size="small" onClick={() => { setDraft(cap != null ? String(cap) : ''); setEditing(true); }} sx={{ p: 0.25 }} disabled={saving}>
                  <EditIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Stack direction="row" spacing={0.25}>
                <IconButton size="small" onClick={commitDraft} sx={{ p: 0.25, color: C.bull }}><CheckIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => setEditing(false)} sx={{ p: 0.25, color: C.bear }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
              </Stack>
            )}
          </Box>
          {editing ? (
            <TextField
              autoFocus
              size="small"
              variant="standard"
              placeholder="Daily-loss cap (₹)"
              value={draft}
              onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') commitDraft(); if (e.key === 'Escape') setEditing(false); }}
              sx={{ mt: 0.5, '& input': { fontSize: CH_TYPE.body, fontWeight: 700, fontVariantNumeric: 'tabular-nums' } }}
              fullWidth
            />
          ) : (
            <>
              <Typography sx={{ ...chTileValueSx, color: statusColor, mt: 0.25 }}>
                {cap == null
                  ? formatCurrency(consumed)
                  : `${formatCurrency(consumed)} / ${formatCurrency(cap)}`}
              </Typography>
              {cap != null && (
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    mt: 0.75,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    '& .MuiLinearProgress-bar': { backgroundColor: statusColor, borderRadius: 3 },
                  }}
                />
              )}
              <Typography variant="caption" sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', mt: 0.25, display: 'block' }}>
                {gate
                  ? (cap == null ? "today's loss · no cap set" : "today's loss vs cap")
                  : (cap == null ? "today's realised loss · no cap set" : "today's realised loss vs cap")}
              </Typography>
            </>
          )}
        </Box>

        {!capOnly && (
          <>
            {portfolioValue != null && (
              <Tile
                icon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
                label="Portfolio value"
                tooltip="Total portfolio value = cash + holdings + open positions across all connected brokers."
                value={formatCurrency(portfolioValue)}
              />
            )}
            <Tile
              icon={<ShowChartIcon sx={{ fontSize: 14 }} />}
              label="Open risk"
              tooltip={unbounded
                ? `${positionsWithoutSl} open position${positionsWithoutSl > 1 ? 's have' : ' has'} NO stop loss, so the loss if price keeps moving is not capped. The stopped positions sum to ${formatCurrency(openRiskSigned)} at their stops. Set a stop on each position to get a bounded figure.`
                : 'Net outcome if every OPEN trade hit its stop right now = sum of the SL Risk (₹) column across all open trades. Negative (red) = net loss at stops; positive (green) = stops are already in profit.'}
              value={unbounded
                ? <span>Unbounded<Box component="span" sx={{ display: 'block', fontSize: '0.62em', fontWeight: 600, whiteSpace: 'nowrap', mt: 0.2 }}>{positionsWithoutSl} position{positionsWithoutSl > 1 ? 's' : ''} without SL</Box></span>
                : formatCurrency(openRiskSigned)}
              valueColor={unbounded || openRiskSigned < 0 ? C.bear : openRiskSigned > 0 ? C.bull : 'text.primary'}
              bg={unbounded || openRiskSigned < 0 ? C.bearMuted : openRiskSigned > 0 ? C.bullMuted : undefined}
              borderColor={unbounded || openRiskSigned < 0 ? C.bearBorder : openRiskSigned > 0 ? C.bullBorder : undefined}
            />
            <Tile
              icon={<ShowChartIcon sx={{ fontSize: 14 }} />}
              label="Today P&L"
              tooltip="Sum of realised P&L of all trades CLOSED today (open positions' unrealised P&L is excluded)."
              value={formatCurrency(todayPnl)}
              valueColor={todayPnl >= 0 ? C.bull : C.bear}
            />
            <Tile
              icon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
              label="Available margin"
              tooltip={availableMargin <= 0
                ? 'Free margin across connected brokers is ZERO — every rupee is committed as margin. New trades will be rejected and any adverse move eats directly into your liquidation buffer.'
                : 'Free margin across connected brokers'}
              /* On 2026-09-08 this tile read ₹0 for 3h44m in plain text and nobody
                 noticed. Zero free margin is not a neutral fact, so it is coloured. */
              value={formatCurrency(availableMargin)}
              valueColor={availableMargin <= 0 ? C.bear : undefined}
            />
            <Tile
              icon={<GppMaybeIcon sx={{ fontSize: 14 }} />}
              label="Liquidation risk"
              tooltip={liqTooltip}
              value={liqValue}
              valueColor={liqColor}
              bg={liqSevere ? alpha(liqColor, isDark ? 0.18 : 0.1) : undefined}
              borderColor={liqSevere ? alpha(liqColor, 0.5) : undefined}
            />
            <Tile
              icon={<ShowChartIcon sx={{ fontSize: 14 }} />}
              label="Open positions"
              value={openPositions}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default RiskCockpit;
